import React, { useState, useRef, ChangeEvent, useEffect } from "react";
import Image from "next/image";
import { useImage } from "@/utils/useProfileImg";
import { UserType } from "@/types/baseTypes";
import { getImage } from "@/utils/serviceAPI/image";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import buttonStyles from "@/components/Global/Button/Button.module.scss";
import styles from "./Profile.module.scss";
import BackgroundBubbles from "@/components/BackgroundBubbles/BackgroundBubbles";

export interface PreviewState {
  file: File | undefined;
  path?: string;
  delete: boolean;
}

interface ProfilePictureProps {
  user: UserType | null;
  preview: PreviewState;
  setPreview: React.Dispatch<React.SetStateAction<PreviewState>>;
}

export default function ProfilePicture({
  user,
  preview,
  setPreview,
}: ProfilePictureProps): JSX.Element {
  const { getLabel, getIcon } = useGlobalContext();
  const [pfp, setPfp] = useState<
    { base64: string; type: string } | undefined
  >();

  useEffect(() => {
    async function fetchImage() {
      if (user && user.profilePicture) {
        const fetchedImage = await getImage(user.profilePicture);
        setPfp({ base64: fetchedImage.base64, type: fetchedImage.type });
      }
    }
    fetchImage();
  }, [user, user?.profilePicture]);

  const pfpSrc = useImage(undefined, pfp);

  const [status, setStatus] = useState<string>("");

  const hiddenUploadBtn = useRef<HTMLInputElement>(null);

  function handleClick(): void {
    hiddenUploadBtn.current?.click();
  }

  async function handleFileUpload(
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const file = event.target.files?.[0];

    if (!file) {
      setStatus(getLabel("upload_image_error"));
      return;
    }

    if (file.size > 5242880) {
      setStatus(getLabel("upload_image_too_big"));
      return;
    }

    const supportedMimeTypes = ["image/jpeg", "image/png"];

    if (!supportedMimeTypes.includes(file.type)) {
      setStatus(getLabel("upload_image_not_supported"));
      return;
    }

    setPreview((state) => ({ ...state, file: file, delete: false }));
    setStatus(""); // Clear previous status on successful file selection
  }

  async function handleImageDeletion(path: string | undefined): Promise<void> {
    if (path) {
      setPreview({
        file: undefined,
        path: path,
        delete: true,
      });
      setStatus(""); // Clear previous status on successful deletion attempt
    }
  }

  const RemoveImageButton = ({ url }: { url: string }) => (
    <>
      <figure>
        <Image
          id="profile-picture"
          src={url}
          height={150}
          width={150}
          alt={"Profile Picture"}
        />
      </figure>{" "}
      <button
        className={`button-reset ${styles["remove-btn"]}`}
        onClick={(e) => {
          e.preventDefault();
          if (user && user.profilePicture) {
            handleImageDeletion(user.profilePicture);
          }
        }}
        aria-label={getLabel("remove_image")}
      >
        <ReactSVG
          wrapper="svg"
          src={getIcon("circleClose")}
          className="remove-icon"
          width={20}
          height={20}
          beforeInjection={(svg) => {
            svg.setAttribute("alt", "Close Icon");
            svg.setAttribute("aria-label", "Close Icon");
            svg.setAttribute("aria-hidden", "true");
          }}
          aria-hidden="true"
        />
      </button>
    </>
  );
  return (
    <>
      <section className={styles["photo-outer-wrapper"]}>
        <BackgroundBubbles topLeft={false} />

        <div className={`${styles["photo"]} is-relative`}>
          {preview.file && !preview.delete && (
            <RemoveImageButton url={URL.createObjectURL(preview.file)} />
          )}
          {!preview.file && !preview.delete && pfp?.base64 && pfpSrc && (
            <RemoveImageButton url={pfpSrc} />
          )}
          {((!preview.file && preview.delete) ||
            (!pfp?.base64 && !preview.file)) && (
            <>
              <figure className={styles["profile-placeholder"]}>
                <Image
                  id="profile-picture"
                  src={getIcon("profilePlaceholder")}
                  height={150}
                  width={150}
                  alt={"Profile Picture"}
                />
              </figure>
            </>
          )}
          <button
            className={`${buttonStyles.btn} ${buttonStyles["is-primary"]} ${styles["upload-btn"]} mx-auto`}
            onClick={handleClick}
            aria-label={getLabel("upload_image")}
          >
            <input
              ref={hiddenUploadBtn}
              type="file"
              id="image"
              name="image"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleFileUpload(event);
              }}
              style={{ display: "none" }}
            />
            {getLabel("upload_image")}
            <ReactSVG
              wrapper="svg"
              src={getIcon("circleUpload")}
              className="remove-icon"
              width={20}
              height={20}
              beforeInjection={(svg) => {
                svg.setAttribute("alt", "Upload Icon");
                svg.setAttribute("aria-label", "Upload Icon");
              }}
            />
          </button>
        </div>
      </section>
      {status && (
        <p role="status" className={`has-text-centered pb-0 mt-6`}>
          {status}
        </p>
      )}
    </>
  );
}
